import React, { useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, CheckCircle2, Clock, Layers, Smartphone, Server, Sparkles } from 'lucide-react';
import { mockData } from '../mock';
import { Navigation } from '../components/Navigation';
import { FloatingWhatsApp } from '../components/FloatingWhatsApp';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

export const ProjectPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const observerRef = useRef(null);

  const project = useMemo(
    () => mockData.portfolio.find((p) => String(p.id) === String(id)),
    [id]
  );

  useEffect(() => {
    if (!project) {
      navigate('/');
    }
  }, [project, navigate]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.15 }
    );

    const elements = document.querySelectorAll('.observe-animation');
    elements.forEach((el) => observerRef.current?.observe(el));

    return () => observerRef.current?.disconnect();
  }, []);

  if (!project) return null;

  const featureList = project.features || [];
  const quickFacts = [
    { icon: Smartphone, title: 'Platforms', text: 'Mobile + Web experiences' },
    { icon: Layers, title: 'Stack', text: 'React Native, React, Tailwind, Node.js' },
    { icon: Server, title: 'Backend', text: 'Secure APIs, auth, analytics ready' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-black text-white">
      <Navigation />
      <FloatingWhatsApp />

      <main className="max-w-6xl lg:max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <section className="pt-28 pb-16 grid lg:grid-cols-[1.05fr_0.95fr] gap-10 items-center">
          <div className="space-y-8 observe-animation">
            <div className="flex items-center gap-3 text-sm text-white/70">
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors"
              >
                <ArrowLeft size={16} /> Back
              </button>
              <span className="h-[1px] w-10 bg-white/15" />
              <Badge className="bg-white/10 text-white border-white/10">{project.category}</Badge>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-white/60">
                <Sparkles size={14} className="text-neutral-300" />
                Signature build
              </div>
              <h1 className="text-4xl sm:text-5xl font-semibold leading-tight">{project.title}</h1>
              <p className="text-lg text-white/70 max-w-2xl">{project.description}</p>
            </div>
            <div className="flex flex-wrap gap-3">
              {featureList.slice(0, 6).map((feature) => (
                <span key={feature} className="text-xs px-4 py-2 rounded-full bg-white/10 border border-white/10 text-white/80">
                  {feature}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap gap-4">
              <Button
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10"
                onClick={() => window.open(mockData.hero.cta.bookCall.link, '_blank', 'noopener,noreferrer')}
              >
                Book a build call
              </Button>
              {/* <Button
                className="bg-white text-neutral-900 hover:bg-white/90"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                Back to top
                <ArrowUpRight size={18} className="ml-2 arrow-float" />
              </Button> */}
            </div>
          </div>

          <div className="relative observe-animation">
            <div className="absolute -inset-3 bg-gradient-to-br from-neutral-600/35 via-neutral-500/25 to-neutral-700/25 blur-3xl opacity-80"></div>
            <Card className="relative overflow-hidden border border-white/10 bg-white/5 backdrop-blur-xl rounded-3xl shadow-2xl hero-showcase group">
              <div className="hero-sheen" />
              <CardContent className="p-4 sm:p-6">
                <div className="relative h-72 rounded-2xl overflow-hidden border border-white/10">
                  <img src={project.image} alt={project.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute top-4 left-4">
                    <Badge className="bg-white/15 text-white border-white/20">Premium build</Badge>
                  </div>
                  <div className="absolute bottom-4 right-4 text-sm text-white/70 flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-neutral-200" />
                    In production
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-3 mt-6">
                  {quickFacts.map((fact) => {
                    const Icon = fact.icon;
                    return (
                      <div key={fact.title} className="rounded-xl border border-white/10 bg-white/5 p-4 flex items-start gap-3 hover-card observe-animation">
                        <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                          <Icon size={18} className="text-neutral-300" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-white">{fact.title}</p>
                          <p className="text-xs text-white/60">{fact.text}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="py-14 border-t border-white/10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 observe-animation">
            <div className="space-y-3">
              <Badge className="bg-white/10 text-white border-white/10 px-3 py-1 rounded-full w-fit">Build highlights</Badge>
              <h2 className="text-3xl font-semibold">What made this ship premium</h2>
              <p className="text-white/70 max-w-2xl">Polished UX, reliable infra, and measurable success metrics—delivered in a focused sprint.</p>
            </div>
            <div className="flex items-center gap-3 text-white/70 text-sm">
              <Clock size={16} className="text-neutral-300" />
              4-6 week delivery window
            </div>
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {[
              { title: 'Experience', text: 'Responsive flows across mobile and desktop with clean information hierarchy and brand-aligned gradients.' },
              { title: 'Performance', text: 'Optimized assets, API caching, and edge delivery for snappy load times.' },
              { title: 'Operations', text: 'Admin-friendly controls, analytics hooks, and environment-based configurations.' },
              { title: 'Reliability', text: 'Monitored deployments with error tracking and uptime alerts baked in.' }
            ].map((item) => (
              <Card key={item.title} className="bg-white/5 border border-white/10 rounded-2xl hover-card observe-animation">
                <CardContent className="p-6 space-y-3">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 size={18} className="text-neutral-300" />
                    <p className="text-lg font-semibold text-white">{item.title}</p>
                  </div>
                  <p className="text-sm text-white/70 leading-relaxed">{item.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section className="py-14 border-t border-white/10">
          <div className="space-y-4 observe-animation">
            <Badge className="bg-white/10 text-white border-white/10 px-3 py-1 rounded-full w-fit">Process</Badge>
            <h2 className="text-3xl font-semibold">How we shipped it</h2>
            <p className="text-white/70 max-w-2xl">Transparent steps from kickoff to deploy, so you always know where the build stands.</p>
          </div>
          <div className="mt-10 grid gap-4">
            {[
              'Discovery and scope alignment with clear success metrics.',
              'Wireframes to interactive prototypes for fast validation.',
              'Engineering build with tracked milestones and weekly demos.',
              'Deploy, monitor, and iterate with analytics + error reporting.'
            ].map((step, idx) => (
              <div
                key={step}
                className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 hover-card observe-animation"
              >
                <span className="text-sm font-semibold text-neutral-300">0{idx + 1}</span>
                <p className="text-white/80">{step}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="py-14 border-t border-b border-white/10">
          <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-neutral-700/35 via-neutral-500/30 to-neutral-800/30 p-10 md:p-12 observe-animation">
            <div className="absolute -top-16 right-16 h-40 w-40 bg-white/20 blur-3xl" />
            <div className="absolute -bottom-20 left-10 h-48 w-48 bg-neutral-500/30 blur-3xl" />
            <div className="relative flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
              <div className="space-y-3 max-w-2xl">
                <h3 className="text-3xl font-semibold">Want a build like this?</h3>
                <p className="text-white/80">Schedule a call to plan your app, dashboard, and backend launch in one streamlined sprint.</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  className="bg-white text-neutral-900 hover:bg-white/90 px-7 py-6"
                  onClick={() => window.open(mockData.hero.cta.bookCall.link, '_blank', 'noopener,noreferrer')}
                >
                  Book a Call
                  <ArrowUpRight size={18} className="ml-2 arrow-float" />
                </Button>
                <Button
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10"
                  onClick={() => window.open(mockData.contact.whatsappLink, '_blank', 'noopener,noreferrer')}
                >
                  WhatsApp
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};
